import { useEffect, useState } from "react"
import { supabase } from "@/lib/supabase"

// Read-only view of the authoritative commission schedule.
//
// Authority chain (fixed, never inverted): ADR-002 → SPEC-001 → the
// `commission_rates` table (SPEC-001 §5.1). This page is informational ONLY: it
// displays the authoritative schedule read from the database; it never defines
// it and must never become the source of truth. Any change to the schedule
// requires a superseding ADR and a database migration — no Admin UI may alter
// `commission_rates` (owner determination 2026-07-01). The database also denies
// anon/authenticated writes on this table (migration M5), so the read-only
// posture is enforced server-side, not merely in the UI.

interface RateRow {
  level: number
  amount: number
  effective_from: string | null
  notes: string | null
}

const SUB_PRICE = 6.99

export function CommissionRatesPage() {
  const [rates, setRates] = useState<RateRow[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase
      .from("commission_rates")
      .select("level, amount, effective_from, notes")
      .order("level")
      .then(({ data }) => {
        setRates((data as RateRow[] | null) ?? [])
        setLoading(false)
      })
  }, [])

  const total = rates.reduce((s, r) => s + Number(r.amount), 0)

  return (
    <div className="space-y-6 max-w-2xl">
      <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
        Commission rates are governed by <strong>ADR-002</strong> (authoritative compensation
        schedule) and are <strong>read-only</strong>. They cannot be changed from this screen.
        Any change requires a superseding ADR and a database migration.
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-50 flex items-center justify-between">
          <h2 className="text-sm font-bold text-gray-900 uppercase tracking-wider">💵 Rate Configuration</h2>
          <div className="text-right">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Total per sub</p>
            <p className="text-xl font-black text-gray-900 tabular-nums">${total.toFixed(2)}</p>
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              {["Level", "Notes", "Amount ($)", "% of $6.99", "Effective"].map(h => (
                <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-6 py-6 text-sm text-gray-400">Loading…</td></tr>
            ) : rates.length === 0 ? (
              <tr><td colSpan={5} className="px-6 py-6 text-sm text-gray-400">No commission schedule configured.</td></tr>
            ) : rates.map(r => (
              <tr key={r.level} className="border-t border-gray-50">
                <td className="px-6 py-3">
                  <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-zimrah-100 text-zimrah-700 text-xs font-black">
                    {r.level}
                  </span>
                </td>
                <td className="px-6 py-3 text-sm font-semibold text-gray-700">{r.notes ?? "—"}</td>
                <td className="px-6 py-3 text-sm font-bold text-gray-900 tabular-nums">${Number(r.amount).toFixed(2)}</td>
                <td className="px-6 py-3 text-sm text-gray-500 tabular-nums">
                  {((Number(r.amount) / SUB_PRICE) * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-3 text-sm text-gray-500 tabular-nums">
                  {r.effective_from ? new Date(r.effective_from).toLocaleDateString() : "—"}
                </td>
              </tr>
            ))}
          </tbody>
          {rates.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-gray-200 bg-gray-50">
                <td colSpan={2} className="px-6 py-4 text-sm font-black text-gray-900">TOTAL PAYOUT</td>
                <td className="px-6 py-4 font-black text-zimrah-700 tabular-nums text-lg">${total.toFixed(2)}</td>
                <td className="px-6 py-4 font-black text-gray-500 tabular-nums">
                  {((total / SUB_PRICE) * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-4"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
